import {ClipboardEvent, useMemo, useRef} from "react";
import ReactQuill, {Quill} from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  // Uploads a file and resolves to its url. When set, images and attachments can be inserted.
  onUpload?: (file: File) => Promise<string | undefined>;
}

type DeltaOp = { insert?: unknown; attributes?: Record<string, unknown> };
type DeltaLike = { ops: DeltaOp[] };
type UploadKind = 'image' | 'file';

const IMAGE_MIMETYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const Delta = Quill.import('delta') as new () => DeltaLike;

// Quill has no attachment icon; a paperclip in the snow theme's stroke style.
(Quill.import('ui/icons') as Record<string, string>).attachment =
  '<svg viewBox="0 0 18 18"><path class="ql-stroke" d="M12.5 6.5l-5 5a1.5 1.5 0 0 0 2.1 2.1l5.3-5.3' +
  'a3 3 0 0 0-4.2-4.2L5.3 9.5a4.5 4.5 0 0 0 6.4 6.4l3.8-3.8"/></svg>';

// Text copied out of a web page carries its computed text color and white background as inline
// styles, which paint white strips over tinted block styles. Deliberate colors survive.
const NEUTRAL_COLOR = /^(black|windowtext|#000(000)?|#2c3e50|rgba?\(\s*0\s*,\s*0\s*,\s*0\s*(,\s*[\d.]+\s*)?\)|rgb\(\s*44\s*,\s*62\s*,\s*80\s*\))$/i;
const NEUTRAL_BACKGROUND = /^(white|transparent|#fff(fff)?|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*[\d.]+\s*)?\))$/i;

const dropNeutralColors = (_node: Node, delta: DeltaLike) => {
  for (const op of delta.ops) {
    if (!op.attributes) continue;
    const {color, background} = op.attributes;
    if (typeof color === 'string' && NEUTRAL_COLOR.test(color)) delete op.attributes.color;
    if (typeof background === 'string' && NEUTRAL_BACKGROUND.test(background)) delete op.attributes.background;
    if (Object.keys(op.attributes).length === 0) delete op.attributes;
  }
  return delta;
};

// Pasted html can reference images by data:/file:/blob: urls; data urls would bloat the saved text
// and the others never load. Pasted image files are uploaded instead (see onPasteCapture).
const dropInlineImages = (node: Node, delta: DeltaLike) => {
  const src = (node as HTMLImageElement).getAttribute('src') ?? '';
  return /^(data|file|blob):/i.test(src) ? new Delta() : delta;
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];

export const RichTextEditor = ({value, onChange, onUpload}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const onUploadRef = useRef(onUpload);
  onUploadRef.current = onUpload;
  const canUpload = !!onUpload;

  // Stable across renders: react-quill re-creates the editor whenever `modules` changes.
  const {modules, insertUploads} = useMemo(() => {
    const insertUploads = async (files: File[], kind: UploadKind) => {
      for (const file of files) {
        const url = await onUploadRef.current?.(file);
        const quill = quillRef.current?.getEditor();
        if (!url || !quill) continue;
        const index = quill.getSelection(true)?.index ?? quill.getLength();
        if (kind === 'image') {
          quill.insertEmbed(index, 'image', url, 'user');
          quill.setSelection(index + 1, 'silent');
        } else {
          quill.insertText(index, file.name, {link: url}, 'user');
          quill.insertText(index + file.name.length, ' ', {link: false}, 'user');
          quill.setSelection(index + file.name.length + 1, 'silent');
        }
      }
    };

    const pickFiles = (kind: UploadKind) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      if (kind === 'image') input.accept = IMAGE_MIMETYPES.join(',');
      input.onchange = () => insertUploads(Array.from(input.files ?? []), kind);
      input.click();
    };

    const modules = {
      toolbar: {
        container: [
          [{header: [1, 2, 3, false]}],
          ["bold", "italic", "underline", "strike"],
          [{color: []}, {background: []}],
          [{list: "ordered"}, {list: "bullet"}],
          canUpload ? ["link", "image", "attachment"] : ["link"],
          ["clean"],
        ],
        handlers: {
          image: () => pickFiles('image'),
          attachment: () => pickFiles('file'),
        },
      },
      // Dropped image files; the default handler would inline them as base64.
      uploader: {
        mimetypes: IMAGE_MIMETYPES,
        handler: (range: { index: number } | null, files: File[]) => {
          if (range) quillRef.current?.getEditor().setSelection(range.index, 'silent');
          insertUploads(files, 'image');
        },
      },
      clipboard: {
        matchers: [
          [Node.ELEMENT_NODE, dropNeutralColors],
          ['IMG', dropInlineImages],
        ],
      },
      // Typing "1. " or "- " stays plain text, so lists can be numbered by hand.
      keyboard: {
        bindings: {'list autofill': null},
      },
    };
    return {modules, insertUploads};
  }, [canUpload]);

  // Quill ignores clipboard files whenever html is present, which is the case when copying an image
  // from a browser. Upload image-only pastes; pastes with text (e.g. from Word, which also attaches a
  // rendered image) go through Quill as usual.
  const onPasteCapture = (e: ClipboardEvent<HTMLDivElement>) => {
    if (!onUploadRef.current) return;
    const images = Array.from(e.clipboardData.files).filter(file => IMAGE_MIMETYPES.includes(file.type));
    if (images.length === 0) return;
    const html = e.clipboardData.getData('text/html');
    const text = html
      ? new DOMParser().parseFromString(html, 'text/html').body.textContent
      : e.clipboardData.getData('text/plain');
    if (text?.trim()) return;
    e.preventDefault();
    insertUploads(images, 'image');
  };

  return (
    <div onPasteCapture={onPasteCapture}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder="Enter content here..."
        style={{
          borderRadius: "4px",
        }}
      />
    </div>
  );
};
